// src/pages/internal/clientesSwap/anexos/index.js
import { useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardMedia,
    CardContent,
    IconButton,
    Paper,
    CardActionArea,
    Alert,
    useTheme
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDropzone from "./fileDropzone";
import { AttachFile, UploadFile } from '@mui/icons-material';

export const MAX_TOTAL_SIZE_BYTES = 50 * 1024 * 1024; // 100 MB (soma de todos os anexos)

export const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
};

export const getFileSize = (file) => {
    if (typeof file?.size === "number") return file.size;
    if (typeof file?.originalFile?.size === "number") return file.originalFile.size;
    if (typeof file?.tamanho === "number") return file.tamanho;
    return 0;
};

export default function UploadScreen({ clientesSwapAnexos, setClientesSwapAnexos, editMode, createMode, setSnackbar }) {

    // Filtra os anexos para mostrar apenas os que não são KMZ
    const anexosGerais = useMemo(() => {
        return (clientesSwapAnexos || []).filter(file => {
            const fileName = file.name || file.nome || '';
            return !fileName.toLowerCase().endsWith('.kmz');
        });
    }, [clientesSwapAnexos]);

    const theme = useTheme()
    // SEPARAÇÃO GARANTIDA:
    // Arquivos novos recebem a flag 'isNew' no momento do upload. Arquivos do banco não têm essa flag.
    const newFiles = anexosGerais.filter(file => file.isNew);
    const savedFiles = anexosGerais.filter(file => !file.isNew);

    const totalSize = useMemo(
        () => (clientesSwapAnexos || []).reduce((sum, f) => sum + getFileSize(f), 0),
        [clientesSwapAnexos]
    );
    const exceedsLimit = totalSize > MAX_TOTAL_SIZE_BYTES;

    const processFiles = useCallback((acceptedFiles) => {
        const nonKmzFiles = acceptedFiles.filter(file => !file.name.toLowerCase().endsWith('.kmz'));

        const addedFiles = nonKmzFiles.map(file => ({
            originalFile: file,
            name: file.name,
            type: file.type,
            size: file.size,
            preview: URL.createObjectURL(file),
            isPdf: file.type === 'application/pdf',
            isNew: true // <--- MARCADOR PARA IDENTIFICAR QUE É NOVO
        }));

        if (addedFiles.length > 0) {
            setClientesSwapAnexos(prevFiles => [...prevFiles, ...addedFiles]);
        }
    }, [setClientesSwapAnexos]);

    // ==========================================
    // CAPTURA DO CTRL+V GLOBAL
    // ==========================================
    useEffect(() => {
        const handlePaste = (event) => {
            // Ignora se o usuário estiver digitando dentro de um campo de texto normal
            const isTextInput = event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA';
            if (isTextInput && event.target.type !== 'file') return;

            if (event.clipboardData && event.clipboardData.files.length > 0) {
                event.preventDefault();
                const pastedFiles = Array.from(event.clipboardData.files);
                processFiles(pastedFiles);
            }
        };

        // Escuta no document inteiro para não falhar
        document.addEventListener('paste', handlePaste);

        return () => {
            document.removeEventListener('paste', handlePaste);
        };
    }, [processFiles]);

    const handleDrop = (event) => {
        event.preventDefault();
        const droppedFiles = Array.from(event.dataTransfer.files);
        processFiles(droppedFiles);
    };

    const handleInputChange = (event) => {
        const selectedFiles = Array.from(event.target.files);
        processFiles(selectedFiles);
        // Reseta o input para permitir re-selecionar o mesmo arquivo após removê-lo.
        event.target.value = "";
    };

    const handleRemoveFile = (fileToRemove) => {
        setClientesSwapAnexos(prevFiles => {
            const updatedFiles = prevFiles.filter(f => f !== fileToRemove);
            if (fileToRemove.preview) {
                URL.revokeObjectURL(fileToRemove.preview);
            }
            return updatedFiles;
        });
    };

    const handleFileClick = (file) => {
        if (editMode || createMode) return;

        const isImage = file.type ? file.type.startsWith('image/') : !file.isPdf;

        if (isImage) {
            const newWindow = window.open();
            if (newWindow) {
                newWindow.document.write(`
                    <html>
                        <head><title>${file.name || file.nome}</title></head>
                        <body style="margin: 0; display: flex; justify-content: center; align-items: center; background-color: #0e0e0e; min-height: 100vh;">
                            <img src="${file.preview}" alt="${file.name || file.nome}" style="max-width: 100%; max-height: 100vh; object-fit: contain;" />
                        </body>
                    </html>
                `);
                newWindow.document.close();
            }
        } else {
            const link = document.createElement('a');
            link.href = file.preview;
            link.download = file.name || file.nome || 'documento';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <Box sx={{ width: '100%', margin: '0 auto' }}>
            {(createMode || editMode) && (
                <>

                    <Typography variant="h6" gutterBottom>
                        Envio de Documentos
                    </Typography>

                    {exceedsLimit && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                            O tamanho total dos anexos é {formatBytes(totalSize)} e excede o limite
                            de {formatBytes(MAX_TOTAL_SIZE_BYTES)}. Remova arquivos até ficar dentro
                            do limite para conseguir salvar.
                        </Alert>
                    )}

                    <FileDropzone
                        files={newFiles} // Passa apenas os que estão como "isNew: true"
                        onDrop={handleDrop}
                        onDragOver={(e) => e.preventDefault()}
                        onFileChange={handleInputChange}
                        onRemoveFile={handleRemoveFile}
                    />

                </>
            )}

            {/* FORA DO QUADRADO: SOMENTE ARQUIVOS ANTIGOS/SALVOS */}
            {savedFiles.length > 0 && (
                <Box mt={createMode || editMode ? 2 : 0}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <AttachFile fontSize='small' sx={{ mr: 1 }} />
                        <Typography variant="subtitle1" fontWeight="bold">
                            Anexos Salvos
                        </Typography>
                    </Box>
                    <Paper variant='outlined' sx={{ p: 2, borderRadius: 5, backgroundColor: theme.palette.background.wallpaper }}>
                        <Grid container spacing={2}>
                            {savedFiles.map((file, index) => (
                                <Grid item xs={12} sm={6} md={3} key={index}>
                                    <Card sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }} variant="outlined" elevation={0}>

                                        {(editMode || createMode) && (
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveFile(file);
                                                }}
                                                sx={{ position: 'absolute', top: 5, right: 5, backgroundColor: 'rgba(255,255,255,0.8)', zIndex: 10 }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        )}

                                        <CardActionArea
                                            onClick={() => handleFileClick(file)}
                                            disabled={editMode || createMode}
                                            sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start' }}
                                        >
                                            {file.isPdf ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 140, backgroundColor: '#f5f5f5' }}>
                                                    <PictureAsPdfIcon sx={{ fontSize: 60, color: 'error.main' }} />
                                                </Box>
                                            ) : (
                                                <CardMedia
                                                    component="img"
                                                    height="140"
                                                    image={file.preview}
                                                    alt={file.name || file.nome}
                                                    sx={{ objectFit: 'cover' }}
                                                />
                                            )}
                                            <CardContent sx={{ flexGrow: 1, pb: "16px !important" }}>
                                                <Typography variant="body2" noWrap title={file.name || file.nome}>
                                                    {file.name || file.nome}
                                                </Typography>
                                                {getFileSize(file) > 0 && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatBytes(getFileSize(file))}
                                                    </Typography>
                                                )}
                                            </CardContent>
                                        </CardActionArea>

                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </Box>
            )}
        </Box>
    );
}